import React, { FC } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from './input-summary.module.scss';
import MinusIcon from '@app/shared/icons/MinusIcon';
import SvgPlus from '@app/shared/icons/Plus';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { enteredPercentageViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { formatMinMaxField } from '@app/shared/formatters';
import { OpmDlomPerInstrumentDictionary } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { SortedInstrument } from './InputSummaryDlocSection';

interface InputSummaryDlomSectionProps {
  show: boolean | undefined;
  sortedInstruments: SortedInstrument[];
  marketValueDlom: OpmDlomPerInstrumentDictionary;
  showAll: boolean;
  setShowAll: (value: boolean) => void;
}

export const InputSummaryDlomSection: FC<InputSummaryDlomSectionProps> = ({
  show,
  sortedInstruments,
  marketValueDlom,
  showAll,
  setShowAll,
}) => {
  const { l } = useLocale();

  if (!show) {
    return null;
  }

  const showDlomExpander = sortedInstruments.length > 2;
  const secondInstrument = sortedInstruments[1];
  const secondInstrumentHasTranches =
    secondInstrument?.instrumentDlom === null &&
    Object.keys(secondInstrument?.trancheDloms ?? {}).length > 1;

  return (
    <>
      <tr>
        <th className={styles['title']} data-testid="dlom-header">
          {l('_DLOM')}
        </th>
      </tr>
      {sortedInstruments.slice(0, showAll ? undefined : 2).map((instrument, mainIndex) => {
        const instrumentDlom = marketValueDlom[instrument.instrumentId];
        const isSecondInstrument = mainIndex === 1;
        const hasTranches =
          instrumentDlom.instrumentDlom === null &&
          Object.keys(instrumentDlom.trancheDloms).length > 1;

        // Single display value used for instruments without multiple tranches
        const singleDlomSource =
          instrumentDlom.instrumentDlom !== null
            ? instrumentDlom.instrumentDlom
            : instrumentDlom.trancheDloms[Object.keys(instrumentDlom.trancheDloms)[0]];
        const singleDlomValue = formatMinMaxField(singleDlomSource);
        const showSingleValue =
          instrumentDlom.instrumentDlom !== null ||
          Object.keys(instrumentDlom.trancheDloms).length === 1;

        // Orange line on the boundary row only when the section is collapsed
        const showExpanderForSimpleInstrument =
          isSecondInstrument && showDlomExpander && !secondInstrumentHasTranches;
        const instrumentRowStyle = showExpanderForSimpleInstrument
          ? 'expand-title-primary'
          : 'expand-title-primary-light';
        const instrumentValueStyle = showExpanderForSimpleInstrument
          ? 'expand-value-primary'
          : 'expand-value-primary-light';

        return (
          <React.Fragment key={`fragment-${mainIndex}`}>
            <tr key={instrument.instrumentId}>
              <td
                className={`${styles[instrumentRowStyle]} ${styles['title']} ${styles['no-border']}`}>
                {instrument.instrumentNarrative}
              </td>
              <td
                className={`${styles[instrumentValueStyle]} ${styles['no-border']} table-primary__cell--right`}
                data-testid={`${instrument.instrumentId}-instrument-dlom`}>
                {showSingleValue && (
                  <CellValue
                    value={singleDlomValue}
                    className={styles['value']}
                    {...(typeof singleDlomValue === 'number' ? enteredPercentageViewRules : {})}
                    alignment={Alignment.Right}
                  />
                )}
              </td>
            </tr>
            {showExpanderForSimpleInstrument && (
              <div className={styles['expander']}>
                <button
                  aria-label={l('_ToggleSectionCollapse', { section: l('_Instruments') })}
                  aria-expanded={showAll}
                  onClick={() => setShowAll(!showAll)}
                  className={styles['expander-button']}
                  data-testid="expander-button-instrument">
                  {showAll ? (
                    <MinusIcon data-testid="minus-icon-instrument" />
                  ) : (
                    <SvgPlus data-testid="svg-plus-instrument" />
                  )}
                </button>
              </div>
            )}
            {hasTranches &&
              Object.keys(instrumentDlom.trancheDloms).map((trancheKey, index, array) => {
                const isLastTranche = index === array.length - 1;
                const showTrancheExpander = isSecondInstrument && showDlomExpander && isLastTranche;
                const trancheDlomValue = formatMinMaxField(instrumentDlom.trancheDloms[trancheKey]);
                const trancheTitleClass = [
                  showTrancheExpander && styles['expand-title-primary'],
                  styles['expand-title-tranche'],
                  styles['no-border'],
                ]
                  .filter(Boolean)
                  .join(' ');
                const trancheValueClass = [
                  showTrancheExpander && styles['expand-value-primary'],
                  styles['value'],
                  styles['no-border'],
                  'table-primary__cell--right',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <tr key={`${instrument.instrumentId}-${trancheKey}`}>
                    <td className={trancheTitleClass}>{`Tranche ${index + 1}`}</td>
                    <td
                      className={trancheValueClass}
                      data-testid={`${instrument.instrumentId}-tranche-${index + 1}`}>
                      <CellValue
                        value={trancheDlomValue}
                        className={styles['value']}
                        {...(typeof trancheDlomValue === 'number'
                          ? enteredPercentageViewRules
                          : {})}
                        alignment={Alignment.Right}
                      />
                    </td>
                  </tr>
                );
              })}
            {isSecondInstrument && showDlomExpander && secondInstrumentHasTranches && (
              <div className={styles['expander']}>
                <button
                  aria-label={l('_ToggleSectionCollapse', { section: l('_Instruments') })}
                  aria-expanded={showAll}
                  onClick={() => setShowAll(!showAll)}
                  className={styles['expander-button']}
                  data-testid="expander-button-tranche">
                  {showAll ? (
                    <MinusIcon data-testid="minus-icon-tranche" />
                  ) : (
                    <SvgPlus data-testid="svg-plus-tranche" />
                  )}
                </button>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};
